import React, { forwardRef } from 'react';
import cn from 'classnames';
import {
  TEXT_LINK,
  TEXT_LINK_PARAGRAPH,
  TEXT_LINK_BREADCRUMB,
  LABEL_TYPES,
  COLORS,
} from './classnames';
import { BUTTON } from '../classnames';
import transition from '../../styles/transition';
import Text from '../../Text';
import { getDefaultIconValues, IconItemType } from '../helpers';
import { TextLinkProps } from './types';

const TextLink = forwardRef<any, TextLinkProps>(
  (
    {
      children,
      label,
      type = 'level1',
      htmlType,
      as,
      href,
      leftIcon: leftIconProp,
      rightIcon: rightIconProp,
      className = '',
      style,
      disabled,
      ...other
    },
    ref,
  ) => {
    const textLinkClassNames = cn(BUTTON, {
      'pointer-events-none': disabled,
      [TEXT_LINK]: !disabled,
      [TEXT_LINK_PARAGRAPH]: type.includes('body') || type === 'legals',
      [TEXT_LINK_BREADCRUMB]: type === 'breadcrumb',
    });
    const leftIcon: IconItemType = getDefaultIconValues(leftIconProp, 16);
    const rightIcon: IconItemType = getDefaultIconValues(rightIconProp, 16);
    let Tag: keyof JSX.IntrinsicElements = 'button';

    if (as) {
      Tag = as;
    } else if (href) {
      Tag = 'a';
    }

    if (leftIcon.component || rightIcon.component) {
      return (
        <Tag
          className={cn(
            textLinkClassNames,
            disabled ? 'text-base-lighter' : `text-${COLORS[type]}`,
            className,
          )}
          style={{
            transition: transition('color'),
            ...style,
          }}
          href={href}
          type={Tag === 'button' ? htmlType : undefined}
          disabled={disabled}
          ref={ref}
          {...other}
        >
          {leftIcon.component && (
            <leftIcon.component
              size={leftIcon.size}
              className={cn({
                'fill-current': leftIcon.hasFill,
                'stroke-current': leftIcon.hasStroke,
              })}
              ignoreColor
            />
          )}
          <Text
            className={cn({
              'ml-8': !!leftIcon.component,
              'mr-8': !!rightIcon.component,
            })}
            type={LABEL_TYPES[type]}
            color="inherit"
          >
            {label || children}
          </Text>
          {rightIcon.component && (
            <rightIcon.component
              size={rightIcon.size}
              className={cn({
                'fill-current': rightIcon.hasFill,
                'stroke-current': rightIcon.hasStroke,
              })}
              ignoreColor
            />
          )}
        </Tag>
      );
    }

    return (
      <Text
        className={cn(textLinkClassNames, className)}
        style={{
          transition: transition('color'),
          ...style,
        }}
        as={Tag}
        type={LABEL_TYPES[type]}
        color={disabled ? 'base-lighter' : COLORS[type]}
        href={href}
        htmlType={htmlType}
        disabled={disabled}
        ref={ref}
        {...other}
      >
        {label || children}
      </Text>
    );
  },
);

export default TextLink;
